/**
 * Error Analysis Cache
 *
 * In-memory + localStorage cache for AI error analysis responses.
 * Prevents repeated AI calls for the same error pattern.
 */

import Cookies from "js-cookie";
import { B3OS_ORG_COOKIE_NAME } from "@/lib/authenticatedFetch";

const STORAGE_KEY = "b3os-error-analysis-cache";
const MAX_ENTRIES = 200;
const TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

export interface FixableField {
  fieldName: string;
  currentValue: unknown;
  suggestedValue: unknown;
  explanation: string;
}

export interface ErrorAnalysisResult {
  /** Human-readable explanation of what went wrong */
  analysis: string;
  /** Whether the error is fixable by the user */
  isFixable: boolean;
  /** One-line suggested fix description */
  suggestedFix: string | null;
  /** Fields that can be fixed with suggested values */
  fixableFields: FixableField[];
  /** Timestamp when analysis was performed */
  timestamp: number;
}

interface CacheEntry {
  key: string;
  result: ErrorAnalysisResult;
  accessedAt: number;
}

// In-memory cache for speed
const memoryCache = new Map<string, CacheEntry>();

/**
 * Generate a stable cache key from error context.
 * Uses nodeType + error message + truncated input for deduplication.
 */
export function generateCacheKey(context: {
  nodeType?: string;
  errorMessage: string;
  nodeInput?: Record<string, unknown>;
  nodeConfig?: Record<string, unknown>;
}): string {
  const orgId = Cookies.get(B3OS_ORG_COOKIE_NAME) || "no-org";
  const parts = [orgId, context.nodeType || "unknown", context.errorMessage.slice(0, 500)];

  // Include a hash of input keys (not values, to catch structural similarity)
  if (context.nodeInput) {
    const inputKeys = Object.keys(context.nodeInput).sort().join(",");
    parts.push(inputKeys);
  }

  // Include config values so changing a field (e.g. headers) busts the cache
  if (context.nodeConfig) {
    try {
      parts.push(JSON.stringify(context.nodeConfig));
    } catch {
      parts.push(Object.keys(context.nodeConfig).sort().join(","));
    }
  }

  return simpleHash(parts.join("::"));
}

/**
 * Get cached analysis result, or null if not found/expired.
 */
export function getCachedAnalysis(key: string): ErrorAnalysisResult | null {
  // Check memory cache first
  const memEntry = memoryCache.get(key);
  if (memEntry && Date.now() - memEntry.result.timestamp < TTL_MS) {
    memEntry.accessedAt = Date.now();
    return memEntry.result;
  }

  // Fall back to localStorage
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;

    const entries: CacheEntry[] = JSON.parse(stored);
    const entry = entries.find(e => e.key === key);
    if (!entry || Date.now() - entry.result.timestamp >= TTL_MS) return null;

    // Promote to memory cache
    entry.accessedAt = Date.now();
    memoryCache.set(key, entry);
    return entry.result;
  } catch {
    return null;
  }
}

/**
 * Remove a cached analysis entry (for cache-busting / re-analysis).
 */
export function removeCachedAnalysis(key: string): void {
  memoryCache.delete(key);
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const entries: CacheEntry[] = JSON.parse(stored);
      const filtered = entries.filter(e => e.key !== key);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
    }
  } catch {
    // Ignore
  }
}

/**
 * Store analysis result in cache.
 */
export function setCachedAnalysis(key: string, result: ErrorAnalysisResult): void {
  const entry: CacheEntry = { key, result, accessedAt: Date.now() };

  // Update memory cache
  memoryCache.set(key, entry);

  // Persist to localStorage with LRU eviction
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    let entries: CacheEntry[] = stored ? JSON.parse(stored) : [];

    // Remove existing entry for this key
    entries = entries.filter(e => e.key !== key);

    // Add new entry
    entries.push(entry);

    // Remove expired entries
    entries = entries.filter(e => Date.now() - e.result.timestamp < TTL_MS);

    // LRU eviction if over limit
    if (entries.length > MAX_ENTRIES) {
      entries.sort((a, b) => b.accessedAt - a.accessedAt);
      entries = entries.slice(0, MAX_ENTRIES);
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // localStorage might be full or unavailable — memory cache still works
  }
}

/** Simple string hash for cache keys */
function simpleHash(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash |= 0; // Convert to 32bit integer
  }
  return `err_${Math.abs(hash).toString(36)}`;
}
